/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { connect } from "react-redux";

// react-router components
import { useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 PRO React examples
import Breadcrumbs from "components/Breadcrumbs";

// Images
import brandWhite from "assets/images/logotipo-onna.png";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "components/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

function DashboardNavbar({
  absolute,
  light,
  isMini,
  route,
  isClient,
  title,
  firstLink,
  secondLink,
  badgeNumber,
}) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const navigate = useNavigate();

  const manualRoute = route?.split("/").slice(1);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox
          alignItems="center"
          color="inherit"
          mb={{ xs: 1, md: 0 }}
          sx={(theme) => navbarRow(theme, { isMini })}
        >
          <IconButton
            sx={navbarDesktopMenu}
            onClick={() => {
              navigate(-1);
            }}
            size="small"
            disableRipple
          >
            <Icon fontSize="medium" sx={iconsStyle}>
              arrow_back
            </Icon>
          </IconButton>
          <IconButton
            sx={navbarDesktopMenu}
            style={{
              marginRight: "2rem",
            }}
            onClick={() => {
              navigate(1);
            }}
            size="small"
            disableRipple
          >
            <Icon fontSize="medium" sx={iconsStyle}>
              arrow_forward
            </Icon>
          </IconButton>
          {!isClient && (
            <Breadcrumbs
              icon="home"
              title={title}
              route={manualRoute}
              light={light}
              firstLink={firstLink}
              secondLink={secondLink}
            />
          )}
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox
              component="img"
              src={brandWhite}
              alt="Brand"
              width="4rem"
              style={{ marginRight: 8 }}
            />
            <MDBox color={light ? "white" : "inherit"}>
              <IconButton
                sx={navbarIconButton}
                size="small"
                disableRipple
                onClick={() => {
                  navigate(isClient ? "/client/account" : "/account");
                }}
              >
                <Icon sx={iconsStyle}>account_circle</Icon>
              </IconButton>
              {!isClient && (
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={handleConfiguratorOpen}
                >
                  <Icon sx={iconsStyle}>settings</Icon>
                </IconButton>
              )}
              {isClient && (
                <IconButton
                  sx={navbarIconButton}
                  size="small"
                  disableRipple
                  onClick={() => {
                    navigate("/client/apartment");
                  }}
                >
                  <Icon sx={iconsStyle}>dashboard</Icon>
                </IconButton>
              )}
              {/* <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={() => {
                  navigate(isClient ? "/client/notifications" : "/notifications");
                }}
              >
                <MDBadge badgeContent={badgeNumber} color="error" size="xs" circular>
                  <Icon sx={iconsStyle}>notifications</Icon>
                </MDBadge>
              </IconButton> */}
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={() => {
                  navigate(isClient ? "/client/notifications" : "/notifications");
                }}
              >
                <MDBadge badgeContent={badgeNumber} color="error" size="xs" circular>
                  <Icon sx={iconsStyle}>notifications</Icon>
                </MDBadge>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
  badgeNumber: 0,
  route: "",
  title: "",
  isClient: false,
  firstLink: "",
  secondLink: "",
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  isClient: PropTypes.bool,
  badgeNumber: PropTypes.number,
  route: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  title: PropTypes.string,
  firstLink: PropTypes.string,
  secondLink: PropTypes.string,
};

const mapStateToProps = (state) => {
  const {
    notificationsReducer: { badgeNumber = 0, notificationsByApartment = {} } = {},
    mainUserReducer: { isClient = false, apartmentId = null, isProjectManager = false } = {},
  } = state;

  let badgeNumberToSend = badgeNumber;

  let clientNotifications = {};

  if (isClient && isProjectManager) {
    clientNotifications = notificationsByApartment?.[apartmentId]?.notifications || {};

    badgeNumberToSend = Object.values(clientNotifications).filter(
      (notification) => !notification?.read
    ).length;
  }

  return {
    badgeNumber: badgeNumberToSend,
  };
};

export default connect(mapStateToProps)(DashboardNavbar);
